import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilterBar,
  FilterField,
  PageShell,
  Pagination,
  RadixSelect,
  RowActions,
  Skeleton,
  StatusBadge,
  type DataTableColumn,
  type RowActionItem,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import {
  useMileageEarnPoliciesQuery,
  useMileageEarnPolicyMutation,
} from '../hooks/useMileageEarnPoliciesQuery';
import { MILEAGE_WRITE_ROLES } from '../lib/mileageActionRbac';
import type { MileageEarnPolicy, MileagePolicyScope } from '../types';

const PAGE_SIZE = 20;

const SCOPE_OPTIONS: ReadonlyArray<{ value: MileagePolicyScope; label: string }> = [
  { value: 'DEFAULT', label: '전역 기본' },
  { value: 'PRODUCT', label: '상품별' },
];

const formatEarn = (p: MileageEarnPolicy): string =>
  p.earnType === 'RATE' ? `${(p.earnValue / 100).toLocaleString()}%` : `${p.earnValue.toLocaleString()}원`;

const formatValidity = (p: MileageEarnPolicy): string =>
  `${p.validityValue}${p.validityUnit === 'MONTH' ? '개월' : '일'}`;

export const EarnPolicyListPage = () => {
  const navigate = useNavigate();
  const canWrite = useHasRole(MILEAGE_WRITE_ROLES);

  const [scope, setScope] = useState<MileagePolicyScope>('DEFAULT');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<MileageEarnPolicy | null>(null);

  const query = useMileageEarnPoliciesQuery(scope);
  const { softDelete, markDefault } = useMileageEarnPolicyMutation();

  const all = useMemo(() => (query.data ?? []).filter((p) => !p.deleted), [query.data]);
  const items = useMemo(
    () => all.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [all, page],
  );

  const columns: DataTableColumn<MileageEarnPolicy>[] = [
    {
      key: 'name',
      header: '정책명',
      cell: (row) => (
        <span
          className="block max-w-[280px] truncate text-sm font-medium"
          style={{ color: 'var(--content-strong)' }}
          title={row.name}
        >
          {row.name}
        </span>
      ),
    },
    {
      key: 'earn',
      header: '적립',
      width: 140,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatEarn(row)} · {row.earnBasis === 'SALE' ? '판매가' : '공급가'}
        </span>
      ),
    },
    {
      key: 'validity',
      header: '유효기간',
      width: 100,
      cell: (row) => (
        <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {formatValidity(row)}
        </span>
      ),
    },
    {
      key: 'active',
      header: '활성',
      align: 'center',
      width: 80,
      cell: (row) => (
        <StatusBadge variant={row.active ? 'success' : 'neutral'} label={row.active ? '활성' : '비활성'} />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      cell: (row) => {
        if (!canWrite) return null;
        const actions: RowActionItem[] = [
          { key: 'edit', label: '수정', onClick: () => navigate(`/mileage/earn-policies/${row.id}`) },
          ...(scope === 'DEFAULT'
            ? [{ key: 'markDefault', label: '기본으로 설정', onClick: () => markDefault.mutate(row.id) }]
            : []),
          {
            key: 'delete',
            label: '삭제',
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => setDeleteTarget(row),
          },
        ];
        return <RowActions triggerLabel="정책 액션" actions={actions} />;
      },
    },
  ];

  return (
    <PageShell
      title="적립 정책"
      actions={
        canWrite ? (
          <Link to="/mileage/earn-policies/new">
            <Button size="sm">
              <Plus size={15} aria-hidden="true" />
              정책 등록
            </Button>
          </Link>
        ) : undefined
      }
    >
      <FilterBar
        className="mb-4"
        filters={
          <FilterField label="범위">
            <RadixSelect
              value={scope}
              onChange={(v) => {
                setScope(v as MileagePolicyScope);
                setPage(1);
              }}
              options={SCOPE_OPTIONS}
              aria-label="범위"
            />
          </FilterField>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>적립 정책을 불러오지 못했습니다.</p>
          {query.error?.message ? (
            <p className="max-w-md text-xs" style={{ color: 'var(--content-muted)' }}>
              {query.error.message}
            </p>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            다시 시도
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          emptyState="등록된 적립 정책이 없습니다."
          stickyHeader
        />
      )}

      <Pagination
        total={all.length}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        className="mt-4"
      />

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>적립 정책 삭제</DialogTitle>
            <DialogDescription>
              "{deleteTarget?.name ?? ''}" 정책을 삭제(비활성)하시겠습니까?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={softDelete.isPending}
              onClick={() => {
                if (deleteTarget) {
                  softDelete.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
                }
              }}
            >
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
